import { createIntegerRollupCompiler } from './integer-layer-rollup';
import { statusProjectionDependencies } from './status-attack-budget';
import { createFormulaReadContext } from './formula-read-context';
import { applyStatusRootFields, compileStatusRootFields } from './status-root-residue';
import { statusRows, statusItemId } from '../statuses/expiry';
import { record } from '../ability/context';
import { sameJson } from './json-change';

type Row = Record<string, unknown>;
const LIGHT = 'bright_light_dim_light';

/** An applied inventory status has saved rows but no creature computed delta.
 * Finish its light before publishing the normal encounter projection. The same
 * integer/source rollup handles add, stack and last-source removal idempotently. */
export function projectInventoryStatusLight(before: Row, after: Row, definition: (id: string) => Row | null): Row {
    const deps = statusProjectionDependencies(after, definition);
    const read = createFormulaReadContext(deps);
    // A guard-only status must not run a cold rollup: its result normalizes the
    // entire inventory node and detaches the live per-instance charge bucket.
    const lightStatuses = (row: Row) => statusRows(row, 'target').filter(status => {
        const source = record(status.status_arr) ?? definition(statusItemId(status));
        return !source || read.layerValue(source, LIGHT) != null || read.layerValue(source, 'magical_light') != null;
    });
    if (sameJson(lightStatuses(before), lightStatuses(after))) return after;
    const projected = createIntegerRollupCompiler({ ...deps, metadata: { ...deps.metadata, integerLayers: [LIGHT] } }).compile(after) as Row;
    const roots = compileStatusRootFields(before, after, { definition, entries: (row, key) => Object.values(read.layerEntries(row, key)),
        value: read.layerValue, active: () => true, buckets: () => ({}), gearLayers: [], quantityIds: [], gearCounted: () => false });
    applyStatusRootFields(projected, Object.fromEntries(Object.entries(roots).filter(([key]) => key === 'magical_light')));
    return projected;
}
